import { RISK_COLORS, type RiskPrediction, type RealtimeRiskResponse } from '../../types';
import { bandKindOf, bandRateText, SHARE_CAVEAT, seasonalComparison } from '../../lib/riskText';

/** The API answers from the day's published map (daily serving path, W5). */
const DAILY_MAP_SOURCE = 'daily-map';

type PopupData = RiskPrediction | RealtimeRiskResponse;

interface CellPopupProps {
  prediction: PopupData;
  onClose: () => void;
}

const RISK_LABELS: Record<string, string> = {
  Low: 'Baixo',
  Moderate: 'Moderado',
  High: 'Alto',
  Extreme: 'Extremo',
};

function isRealtime(p: PopupData): p is RealtimeRiskResponse {
  return 'weather_source' in p;
}

export default function CellPopup({ prediction, onClose }: CellPopupProps) {
  const color = RISK_COLORS[prediction.risk_level];
  const probability = (prediction.fire_probability * 100).toFixed(1);
  const realtime = isRealtime(prediction);
  const fromDailyMap = realtime && prediction.weather_source === DAILY_MAP_SOURCE;
  const share = bandKindOf(prediction.model_version) === 'share';

  return (
    <div className="rounded-xl border border-ink-700 bg-ink-900 shadow-2xl overflow-hidden">
      {/* Header */}
      <div
        className="px-4 py-3 text-white flex items-center justify-between"
        style={{ backgroundColor: color }}
      >
        <div>
          <div className="font-display font-bold text-lg">
            Risco {RISK_LABELS[prediction.risk_level]}
          </div>
          <div className="text-sm opacity-90">
            {fromDailyMap
              ? 'Mapa de hoje'
              : realtime
                ? 'Tempo real'
                : (prediction as RiskPrediction).distrito || 'Portugal'}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
          aria-label="Fechar"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="p-4 text-mist">
        {/* Band (share mode, decision D8) or probability (legacy) */}
        {share ? (
          <div className="text-center mb-4">
            <div className="text-2xl font-bold font-display" style={{ color }}>
              {RISK_LABELS[prediction.risk_level]}
            </div>
            <div className="mt-1 text-xs text-mist-muted">{bandRateText(prediction.risk_level)}</div>
            <div className="mt-2 text-[11px] leading-snug text-mist-faint">
              {SHARE_CAVEAT} A relação com os fogos reais mede-se na página Validação.
            </div>
          </div>
        ) : (
        <div className="text-center mb-4">
          <div className="text-4xl font-bold font-display" style={{ color }}>
            {probability}%
          </div>
          <div className="text-sm text-mist-muted">Pontuação de risco</div>
          {(() => {
            const lower = 'confidence_lower' in prediction ? (prediction as any).confidence_lower : null;
            const upper = 'confidence_upper' in prediction ? (prediction as any).confidence_upper : null;
            return lower != null && upper != null ? (
              <div className="text-xs text-mist-faint mt-1">
                IC 90%: {(lower * 100).toFixed(0)}% a {(upper * 100).toFixed(0)}%
              </div>
            ) : null;
          })()}
          <div className="mt-2 text-[11px] leading-snug text-mist-faint">
            Pontuação relativa, não frequência observada: o fogo é raro, por isso 5% já é risco
            moderado. Medimos a relação com a realidade na página Validação.
          </div>
        </div>
        )}

        {/* Seasonal comparison (how unusual today is for this place + season) */}
        {realtime && (() => {
          const sc = seasonalComparison((prediction as RealtimeRiskResponse).seasonal_percentile);
          if (!sc) return null;
          return (
            <div
              className={`mb-4 rounded-lg border p-3 ${
                sc.anomaly ? 'border-ember/40 bg-ember/10' : 'border-ink-700 bg-ink-800'
              }`}
            >
              <div className="font-mono text-[11px] uppercase tracking-wider text-mist-faint">
                Comparação com a época
              </div>
              <div className={`mt-1 text-sm font-medium ${sc.anomaly ? 'text-ember' : 'text-mist'}`}>
                {sc.label}
              </div>
              {sc.detail && <div className="mt-0.5 text-xs text-mist-muted">{sc.detail}</div>}
            </div>
          );
        })()}

        {/* Location */}
        <div className="text-sm text-mist-muted mb-4 bg-ink-800 rounded-lg p-3">
          <div className="flex justify-between mb-1">
            <span>Latitude:</span>
            <span className="font-mono text-mist">{prediction.lat.toFixed(4)}</span>
          </div>
          <div className="flex justify-between">
            <span>Longitude:</span>
            <span className="font-mono text-mist">{prediction.lon.toFixed(4)}</span>
          </div>
        </div>

        {/* Fallback warning */}
        {realtime && (prediction as RealtimeRiskResponse).weather_source === 'fallback' && (
          <div className="mb-3 bg-ember/10 border border-ember/30 rounded-lg px-3 py-2 text-xs text-ember">
            <span className="font-semibold">Sem dados meteorológicos em tempo real.</span>{' '}
            A previsão usa valores sazonais estimados.
          </div>
        )}

        {/* Realtime weather info */}
        {realtime && (
          <div>
            <div className="text-sm font-semibold text-mist mb-2">
              {fromDailyMap ? 'Origem' : 'Dados meteorológicos'}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm bg-ink-800 rounded px-2 py-1">
                <span className="text-mist-muted">Fonte</span>
                <span className="font-mono text-mist font-medium">
                  {(prediction as RealtimeRiskResponse).weather_source === 'fallback'
                    ? 'sazonal (estimado)'
                    : fromDailyMap
                      ? 'mapa do dia (uma emissão de manhã)'
                      : (prediction as RealtimeRiskResponse).weather_source}
                </span>
              </div>
              {fromDailyMap && (
                <div className="flex items-center justify-between text-sm bg-ink-800 rounded px-2 py-1">
                  <span className="text-mist-muted">Emitido</span>
                  <span className="font-mono text-mist font-medium">
                    {new Date((prediction as RealtimeRiskResponse).timestamp).toLocaleString('pt-PT', {
                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </span>
                </div>
              )}
              {(prediction as RealtimeRiskResponse).station_distance_km != null && (
                <div className="flex items-center justify-between text-sm bg-ink-800 rounded px-2 py-1">
                  <span className="text-mist-muted">Dist. estação</span>
                  <span className="font-mono text-mist font-medium">
                    {(prediction as RealtimeRiskResponse).station_distance_km!.toFixed(1)} km
                  </span>
                </div>
              )}
              {(prediction as RealtimeRiskResponse).ffmc != null && (
                <div className="flex items-center justify-between text-sm bg-ink-800 rounded px-2 py-1">
                  <span className="text-mist-muted">FFMC</span>
                  <span className="font-mono text-mist font-medium">
                    {(prediction as RealtimeRiskResponse).ffmc!.toFixed(1)}
                  </span>
                </div>
              )}
              {(prediction as RealtimeRiskResponse).data_age_seconds != null && (
                <div className="flex items-center justify-between text-sm bg-ink-800 rounded px-2 py-1">
                  <span className="text-mist-muted">Idade dos dados</span>
                  <span className="font-mono text-mist font-medium">
                    {Math.round((prediction as RealtimeRiskResponse).data_age_seconds! / 60)} min
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contributing factors (grid-based) */}
        {!realtime && (prediction as RiskPrediction).contributing_factors.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-mist mb-2">Fatores principais</div>
            <div className="space-y-1.5">
              {(prediction as RiskPrediction).contributing_factors.slice(0, 5).map((factor, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-ink-800 rounded px-2 py-1">
                  <span className="text-mist-muted">{factor.feature}</span>
                  <span className="font-mono text-mist font-medium">{factor.value.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Meta */}
        <div className="mt-4 pt-4 border-t border-ink-700 text-xs text-mist-faint">
          <div className="flex justify-between">
            <span>Modelo:</span>
            <span className="font-mono">
              {realtime
                ? (prediction as RealtimeRiskResponse).model_version
                : (prediction as RiskPrediction).model_version}
            </span>
          </div>
          {!realtime && (
            <div className="flex justify-between mt-1">
              <span>Previsão para:</span>
              <span className="font-mono">{(prediction as RiskPrediction).for_date}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
